import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { AddOutlined } from "@mui/icons-material";
import DatagridComponent from "../../components/datagridComponent";
import api from "../../services/api";
import useStyles from "./styles";

export default function IntegracaoErp() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [clientesSgp, setClientesSgp] = useState([]);
  const [clientesLocais, setClientesLocais] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [snackbar, setSnackbar] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [resSgp, resLocal] = await Promise.all([
        api.post("/sgp/clientes"),
        api.get("/getClients"),
      ]);
      
      const sgpData = resSgp.data?.clientes || [];
      const localData = resLocal.data || [];

      setClientesSgp(sgpData);
      setClientesLocais(localData);
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: "Erro ao buscar clientes",
      });
    } finally {
      setLoading(false);
    }
  }

  const normalizarCnpj = (cnpj) => {
    if (!cnpj) return "";
    return cnpj.replace(/[./-]/g, "");
  };

  // cliente cadastrado à mão tem o CNPJ mas não o clienteErpId: ainda precisa
  // aparecer para ser vinculado ao ERP
  const isImportado = (cpfcnpj) => {
    const cnpjLimpo = normalizarCnpj(cpfcnpj);
    return clientesLocais.some((c) => c.cnpj === cnpjLimpo && c.clienteErpId);
  };

  const handleImport = async () => {
    if (selectedIds.length === 0) return;

    const clientesParaImportar = clientesSgp.filter((c) =>
      selectedIds.includes(c.id)
    );

    setLoadingImport(true);
    try {
      const response = await api.post("/sgp/importClientes", {
        clientes: clientesParaImportar,
      });

      setSnackbar({
        severity: "success",
        children: `Sucesso: ${response.data.criados} criados, ${response.data.atualizados} atualizados.`,
      });

      setSelectedIds([]);
      // Atualiza a lista local para refletir os novos status
      const resLocal = await api.get("/getClients");
      setClientesLocais(resLocal.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: "Erro ao importar clientes",
      });
    } finally {
      setLoadingImport(false);
    }
  };

  const rowsFiltrados = clientesSgp.filter((c) => !isImportado(c.cpfcnpj));

  const columns = [
    { field: "id", headerName: "ID SGP", width: 90 },
    { field: "nome", headerName: "Razão Social", flex: 1 },
    { field: "nomeFantasia", headerName: "Nome Fantasia", flex: 1 },
    { field: "cpfcnpj", headerName: "CPF/CNPJ", width: 160 },
    {
      field: "cidade",
      headerName: "Cidade/UF",
      flex: 1,
      valueGetter: (value, row) => {
        const endereco = row?.endereco;
        if (endereco && endereco.cidade && endereco.uf) {
          return `${endereco.cidade} / ${endereco.uf}`;
        }
        return "";
      },
    },
    {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      valueGetter: (value, row) => {
        const emails = row?.contatos?.emails;
        if (emails && emails.length > 0) return emails[0];
        return "";
      },
    },
  ];

  return (
    <Box sx={styles.pageBox}>
      {/* Paper externo: engloba título, botões e tabela */}
      <Paper elevation={theme.palette.mode === "dark" ? 0 : 2} sx={styles.outerPaper}>
        {/* Header */}
        <Box sx={styles.headerBox}>
          <Typography variant="h5" sx={styles.title}>
            Integração ERP
          </Typography>
          <Box sx={styles.actionsBox}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddOutlined />}
              onClick={handleImport}
              disabled={selectedIds.length === 0 || loadingImport || loading}
            >
              {loadingImport ? "Importando..." : "Adicionar Selecionados"}
            </Button>
          </Box>
        </Box>

        {/* Paper interno: só a tabela, cor do wallpaper */}
        <Paper elevation={0} sx={styles.innerPaper}>
          <DatagridComponent
            rows={rowsFiltrados}
            columns={columns}
            loading={loading}
            isPagination={true}
            options={{
              checkboxSelection: true,
              onRowSelectionModelChange: (newSelection) => {
                setSelectedIds(newSelection);
              },
              rowSelectionModel: selectedIds,
            }}
          />
        </Paper>
      </Paper>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity}
          sx={{ width: "100%" }}
        >
          {snackbar?.children}
        </Alert>
      </Snackbar>
    </Box>
  );
}
